import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { SellingPricePolicyItem } from '@app/api/price-service';
import { PermissionService } from '@app/core/services/permission.service';
import { PriceListService } from '@app/feature/center-business/goods/price-list/services/price-list.service';

const ORDER = ['WEB', 'POS', 'WHOLESALE', 'DISTRIBUTION', 'MARKET'];

@Component({
  selector: 'app-selling-price-policy',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ToastModule],
  providers: [MessageService],
  templateUrl: './selling-price-policy.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SellingPricePolicyComponent implements OnInit {
  private readonly priceListService = inject(PriceListService);
  private readonly messages = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly permissions = inject(PermissionService);

  readonly canEdit = signal(false);

  readonly items = signal<SellingPricePolicyItem[]>([]);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);

  ngOnInit(): void {
    const refresh = () =>
      this.canEdit.set(this.permissions.canDoAction('settings.price.selling_tabs.edit'));
    this.permissions.permissionsLoaded$.subscribe(() => refresh());
    refresh();
    this.priceListService.listSellingPrices().subscribe({
      next: (items) => {
        this.items.set(this.ordered(items ?? []));
        this.loading.set(false);
      },
      error: () => {
        this.error.set('price_policy.errors.load');
        this.loading.set(false);
      },
    });
  }

  labelKey(code: string): string {
    return `price_list.tabs.${code.toLowerCase()}`;
  }

  setEnabled(code: string, enabled: boolean): void {
    if (!this.canEdit() || this.saving()) {
      return;
    }
    const previous = this.items();
    const next = previous.map((item) =>
      item.code === code ? { ...item, enabled } : item,
    );
    this.items.set(next);
    this.saving.set(true);
    this.priceListService.updateSellingPrices(next).subscribe({
      next: (items) => {
        this.items.set(this.ordered(items ?? []));
        this.saving.set(false);
        this.messages.add({
          severity: 'success',
          summary: this.translate.instant('price_policy.saved'),
        });
      },
      error: () => {
        this.items.set(previous);
        this.saving.set(false);
        this.messages.add({
          severity: 'error',
          summary: this.translate.instant('price_policy.errors.save'),
        });
      },
    });
  }

  private ordered(items: SellingPricePolicyItem[]): SellingPricePolicyItem[] {
    return ORDER.map(
      (code) => items.find((item) => item.code === code) ?? { code, enabled: true },
    );
  }
}
